import React, { useCallback, useEffect, useRef, useState } from 'react'

import type { FeedItem } from '@knocklabs/client'
import { NotificationFeedPopover, UnseenBadge } from '@knocklabs/react'
import { HiOutlineBell } from 'react-icons/hi2'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'

import IconButton from '@/components/common/IconButton'

import { CHANGELOG_WORKFLOW_KEY } from '@/constants/knock'

import {
  useKnockFeed,
  useKnockReady,
} from '@/notifications/knock/components/NotificationProvider'
import type { NotificationFeedProps } from '@/notifications/types'

interface NotificationToastProps {
  buttonRef: React.RefObject<HTMLButtonElement>
}

const NotificationToast: React.FC<NotificationToastProps> = ({ buttonRef }) => {
  const { feedClient } = useKnockFeed()

  useEffect(() => {
    const onNotificationsReceived = ({ items }: { items: FeedItem[] }) => {
      if (items.every((item) => item.source.key === CHANGELOG_WORKFLOW_KEY)) {
        return
      }

      toast.info('You have a new notification.', {
        action: {
          label: 'View',
          onClick: () => {
            buttonRef.current?.click()
          },
        },
      })
    }

    feedClient.on('items.received.realtime', onNotificationsReceived)
    return () => {
      feedClient.off('items.received.realtime', onNotificationsReceived)
    }
  }, [buttonRef, feedClient])

  return null
}

export const NotificationFeed: React.FC<NotificationFeedProps> = ({
  className,
}) => {
  const isKnockReady = useKnockReady()

  const [isVisible, setIsVisible] = useState<boolean>(false)

  const notifButtonRef = useRef<HTMLButtonElement>(null)

  const toggleNotificationFeed = useCallback(() => {
    setIsVisible((visible) => !visible)
  }, [])

  const closeNotificationFeed = () => {
    setIsVisible(false)
  }

  const button = (
    <IconButton
      // DO NOT REMOVE THE ARIA-LABEL, IT'S HOW NotificationFeedPopover
      // IDENTIFIES WHAT TO OPEN THE POPOVER RELATIVE TO
      aria-label="Open notification feed"
      ref={notifButtonRef}
      onClick={toggleNotificationFeed}
      disabled={!isKnockReady}
      className={twMerge(
        !isKnockReady ? 'opacity-50 cursor-wait' : '',
        'flex items-center justify-center',
        className,
      )}
      tooltipContent={
        !isKnockReady ? <span className="text-xs">Loading...</span> : null
      }
    >
      {isKnockReady && (
        // must be ready to show the badge, otherwise Knock will throw an error
        <div className="absolute -top-[3px] -right-[3px]">
          <UnseenBadge />
        </div>
      )}
      <HiOutlineBell className="flex-shrink-0 w-5 h-5 text-primary-900 dark:text-primary-200" />
    </IconButton>
  )

  // If Knock isn't ready yet, just render the disabled button
  if (!isKnockReady) {
    return button
  }

  return (
    <div
      // DO NOT REMOVE THE ARIA-LABEL, IT'S HOW NotificationFeedPopover
      // IDENTIFIES WHAT TO OPEN THE POPOVER RELATIVE TO
      // aria-label="Open notification feed"
      className="relative"
    >
      {button}
      <NotificationToast buttonRef={notifButtonRef} />
      <NotificationFeedPopover
        buttonRef={notifButtonRef}
        isVisible={isVisible}
        onClose={closeNotificationFeed}
      />
    </div>
  )
}
